import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import usePageMeta from './hooks/usePageMeta';
import { useLanguage } from './context/LanguageContext';
import PublicLayout from './components/layout/PublicLayout';
import ErrorBoundary from './components/system/ErrorBoundary';
import ScrollToTop from './components/system/ScrollToTop';

// 首頁維持 eager，避免行動版 PSI 在 `/` 先閃 RouteLoading。
import HomePage from './pages/HomePage';
import RouteLoading from './components/ui/RouteLoading';
import { fetchClient } from './utils/fetchClient';
import { buildAccessProfile } from './utils/accessControl';
import { getAdminRouteDeniedReason } from './constants/adminRouteAccess';
import AdminAccessDenied from './components/system/AdminAccessDenied';
import RequireStudentAuth from './components/student/RequireStudentAuth';
import ToastProvider from './components/ui/ToastProvider';
import WeeklyHomeBanner from './components/layout/WeeklyHomeBanner';
import { fetchCurrentWeeklyReport } from './services/weeklyReportApi';
import { staffLoginPath } from './utils/staffLoginNext';

// 其餘公開／後台頁一律 code-split，避免主包一次下載 FullCalendar、練習遊戲、GSAP 等。
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage'));
const WordBridgePage = lazy(() => import('./pages/WordBridgePage'));
const ListeningLadderPage = lazy(() => import('./pages/ListeningLadderPage'));
const VocabularyDepthPage = lazy(() => import('./pages/VocabularyDepthPage'));
const VocabularySizePage = lazy(() => import('./pages/VocabularySizePage'));
const SpeakingDiagnosticPage = lazy(() => import('./pages/SpeakingDiagnosticPage'));
const SpeakingCheckPage = lazy(() => import('./pages/SpeakingCheckPage'));
const SpeakingHostLoginPage = lazy(() => import('./pages/SpeakingHostLoginPage'));
const SpeakingHostPage = lazy(() => import('./pages/SpeakingHostPage'));
const ActivityPhrasebookPage = lazy(() => import('./pages/ActivityPhrasebookPage'));
const WeeklyPage = lazy(() => import('./pages/WeeklyPage'));
const WeeklyPreviewPage = lazy(() => import('./pages/WeeklyPreviewPage'));
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage'));
const EnglishTableTopicsPage = lazy(() => import('./pages/EnglishTableTopicsPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const AnnouncementDetailPage = lazy(() => import('./pages/AnnouncementDetailPage'));
const MyReservationsPage = lazy(() => import('./pages/MyReservationsPage'));
const NotificationPage = lazy(() => import('./pages/NotificationPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CourseGuidePage = lazy(() => import('./pages/CourseGuidePage'));
const HomeImmersiveTestPage = lazy(() => import('./pages/HomeImmersiveTestPage'));
const CampusJourneyDraftPage = lazy(() => import('./pages/CampusJourneyDraftPage'));
const ScrollWorldPacksPreviewPage = lazy(() => import('./pages/ScrollWorldPacksPreviewPage'));
const LearningResourcesPage = lazy(() => import('./pages/LearningResourcesPage'));
const RegulationsFormsPage = lazy(() => import('./pages/RegulationsFormsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const AdminHome = lazy(() => import('./components/AdminHome'));
const ClassOverview = lazy(() => import('./components/ClassOverview'));
const ViolationManagement = lazy(() => import('./components/ViolationManagement'));
const SurveyPage = lazy(() => import('./components/SurveyPage'));
const SurveyChoicePage = lazy(() => import('./components/SurveyChoicePage'));
const ClassDetail = lazy(() => import('./components/ClassDetail'));
const AccountManagement = lazy(() => import('./components/AccountManagement'));
const ForceResetPassword = lazy(() => import('./components/ForceResetPassword'));
const EnglishTestManagement = lazy(() => import('./components/EnglishTestManagement'));
const EnglishTestRegistrationPage = lazy(() => import('./components/EnglishTestRegistrationPage'));
const LearningPartnerRegistrationPage = lazy(() => import('./components/LearningPartnerRegistrationPage'));
const LearningPartnerStatusPage = lazy(() => import('./components/LearningPartnerStatusPage'));
const LearningPartnerApprovePage = lazy(() => import('./components/LearningPartnerApprovePage'));
const ClassBestepOverview = lazy(() => import('./components/ClassBestepOverview'));
const AdminClassCreditAllocationPage = lazy(() => import('./pages/admin/AdminClassCreditAllocationPage'));
const TeacherDashboardPage = lazy(() => import('./components/TeacherDashboardPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AnalyticsHubPage = lazy(() => import('./pages/admin/AnalyticsHubPage'));
const RiskDetectionPage = lazy(() => import('./components/RiskDetectionPage'));
const ReportPage = lazy(() => import('./components/ReportPage'));
const AnnouncementManagementPage = lazy(() => import('./pages/admin/AnnouncementManagementPage'));
const AnnouncementEditorPage = lazy(() => import('./pages/admin/AnnouncementEditorPage'));
const AdminWeeklyReportPage = lazy(() => import('./pages/admin/AdminWeeklyReportPage'));
const AdminWeeklyReportEditorPage = lazy(() => import('./pages/admin/AdminWeeklyReportEditorPage'));
const StudentContentHubPage = lazy(() => import('./pages/admin/StudentContentHubPage'));
const PageContentLegacyRedirect = lazy(() => import('./pages/admin/PageContentLegacyRedirect'));
const AdminAuditLogsPage = lazy(() => import('./pages/admin/AdminAuditLogsPage'));
const SurveyAdminModulePage = lazy(() => import('./pages/admin/SurveyAdminModulePage'));
const SurveyAdminResponsesPage = lazy(() => import('./pages/admin/SurveyAdminResponsesPage'));
const SurveyAdminStatsPage = lazy(() => import('./pages/admin/SurveyAdminStatsPage'));
const AdminSurveyCenterPage = lazy(() => import('./pages/admin/AdminSurveyCenterPage'));
const AdminSurveyRulesPage = lazy(() => import('./pages/admin/AdminSurveyRulesPage'));
const AdminSurveyResponsesPage = lazy(() => import('./pages/admin/AdminSurveyResponsesPage'));
const AdminSurveyAnalyticsPage = lazy(() => import('./pages/admin/AdminSurveyAnalyticsPage'));
const AdminSurveyDataHealthPage = lazy(() => import('./pages/admin/AdminSurveyDataHealthPage'));
const AdminSurveyAnswerMappingPage = lazy(() => import('./pages/admin/AdminSurveyAnswerMappingPage'));
const AdminDashboardProduct = lazy(() => import('./pages/admin/AdminDashboardProduct'));
const AdminRoleIndex = lazy(() => import('./components/admin/AdminRoleIndex'));
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage'));
const EventTypesAdminPage = lazy(() => import('./pages/admin/EventTypesAdminPage'));
const AdminEmailTemplatesPage = lazy(() => import('./pages/admin/AdminEmailTemplatesPage'));
const InternalDiagnosticsPage = lazy(() => import('./pages/admin/InternalDiagnosticsPage'));
const AdminEventDetailPage = lazy(() => import('./pages/admin/AdminEventDetailPage'));
const AdminEventCheckinKioskPage = lazy(() => import('./pages/admin/AdminEventCheckinKioskPage'));
const AdminEtTaskTemplatesPage = lazy(() => import('./pages/admin/AdminEtTaskTemplatesPage'));
const AdminEtGroupingSettingsPage = lazy(() => import('./pages/admin/AdminEtGroupingSettingsPage'));
const AdminEtGroupingReportsPage = lazy(() => import('./pages/admin/AdminEtGroupingReportsPage'));
const AdminEtStudentTrendsPage = lazy(() => import('./pages/admin/AdminEtStudentTrendsPage'));
const AdminEtLeaderSessionsPage = lazy(() => import('./pages/admin/AdminEtLeaderSessionsPage'));
const AdminEtLeaderPayrollPage = lazy(() => import('./pages/admin/AdminEtLeaderPayrollPage'));
const AdminEventParticipationStatsPage = lazy(() => import('./pages/admin/AdminEventParticipationStatsPage'));
const EnglishTestImportHubPage = lazy(() => import('./pages/admin/EnglishTestImportHubPage'));
const LearningJourneyDashboardPage = lazy(() => import('./pages/admin/LearningJourneyDashboardPage'));
const LearningJourneyImportHubPage = lazy(() => import('./pages/admin/LearningJourneyImportHubPage'));
const AdminEwlSyncPage = lazy(() => import('./pages/admin/AdminEwlSyncPage'));
const AdminOpsScriptsPage = lazy(() => import('./pages/admin/AdminOpsScriptsPage'));
const TripCountPage = lazy(() => import('./pages/admin/TripCountPage'));
const EnglishLearningPassportPage = lazy(() => import('./pages/student/EnglishLearningPassportPage'));
const StudentProgressPage = lazy(() => import('./pages/student/StudentProgressPage'));
const SpeakingPortfolioPage = lazy(() => import('./pages/student/SpeakingPortfolioPage'));
const ClassCreditAllocationPage = lazy(() => import('./pages/student/ClassCreditAllocationPage'));
const StudentLoginPage = lazy(() => import('./pages/student/StudentLoginPage'));
const StudentForgotPasswordPage = lazy(() => import('./pages/student/StudentForgotPasswordPage'));
const StudentRegisterPage = lazy(() => import('./pages/student/StudentRegisterPage'));
const StudentAccountPage = lazy(() => import('./pages/student/StudentAccountPage'));
const EnglishLearningPassportSubmissionPage = lazy(() => import('./pages/student/EnglishLearningPassportSubmissionPage'));
const EnglishLearningPassportCertificationPage = lazy(() => import('./pages/student/EnglishLearningPassportCertificationPage'));
const EnglishLearningPassportsAdminPage = lazy(() => import('./pages/admin/EnglishLearningPassportsAdminPage'));
const EnglishLearningPassportDetailPage = lazy(() => import('./pages/admin/EnglishLearningPassportDetailPage'));
const LearningJourneyOperationsPage = lazy(() => import('./pages/admin/LearningJourneyOperationsPage'));
const LearningJourneyStudentProfilePage = lazy(() => import('./pages/admin/LearningJourneyStudentProfilePage'));
const LearningAnalyticsLayout = lazy(() => import('./pages/admin/LearningAnalyticsLayout'));
const LearningAnalyticsOverviewPage = lazy(() => import('./pages/admin/LearningAnalyticsOverviewPage'));
const LearningAnalyticsCohortsPage = lazy(() => import('./pages/admin/LearningAnalyticsCohortsPage'));
const LearningAnalyticsOfferingsPage = lazy(() => import('./pages/admin/LearningAnalyticsOfferingsPage'));
const LearningAnalyticsResourcesPage = lazy(() => import('./pages/admin/LearningAnalyticsResourcesPage'));
const LearningAnalyticsSkillsPage = lazy(() => import('./pages/admin/LearningAnalyticsSkillsPage'));
const LearningAnalyticsB1PlusPage = lazy(() => import('./pages/admin/LearningAnalyticsB1PlusPage'));
const LearningAnalyticsStudentExplorerPage = lazy(() => import('./pages/admin/LearningAnalyticsStudentExplorerPage'));
const LearningAnalyticsTrackingCohortsPage = lazy(() => import('./pages/admin/LearningAnalyticsTrackingCohortsPage'));
const LearningAnalyticsStudentJourneyPage = lazy(() => import('./pages/admin/LearningAnalyticsStudentJourneyPage'));
const LearningAnalyticsStudentDetailPage = lazy(() => import('./pages/admin/LearningAnalyticsStudentDetailPage'));
const LearningAnalyticsRawDataPage = lazy(() => import('./pages/admin/LearningAnalyticsRawDataPage'));
const LearningAnalyticsSettingsPage = lazy(() => import('./pages/admin/LearningAnalyticsSettingsPage'));
const LearningAnalyticsKpiReportPage = lazy(() => import('./pages/admin/LearningAnalyticsKpiReportPage'));
const ImportCenterPage = lazy(() => import('./pages/admin/ImportCenterPage'));
const ImportRunHistoryPage = lazy(() => import('./pages/admin/ImportRunHistoryPage'));
const SpeakingDiagnosticAdminPage = lazy(() => import('./pages/admin/SpeakingDiagnosticAdminPage'));

/** 舊版後台 URL → 現行入口，並附帶一次性 migrated 提示 */
function AdminLegacyRedirect({ to, fromLabel }) {
  return <Navigate to={to} replace state={{ adminMigratedFrom: fromLabel }} />;
}

/** 舊版班級明細 URL → Phase 1 新 path（納入 AdminLayout） */
function LegacyClassDetailRedirect({ token }) {
  const { classId } = useParams();
  const returnTo = useLocation();
  if (!token) return <Navigate to={staffLoginPath(`${returnTo.pathname}${returnTo.search}`)} replace />;
  const route = `/admin/classes/${classId}/detail`;
  const accessProfile = buildAccessProfile(token || '', localStorage.getItem('userRole') || '');
  const denied = getAdminRouteDeniedReason(accessProfile, route);
  if (denied) {
    return (
      <AdminAccessDenied
        route={denied.route}
        rule={denied.rule}
        missingRule={denied.code === 'missing_rule'}
      />
    );
  }
  return (
    <Navigate
      to={`/admin/classes/${classId}`}
      replace
      state={{ adminMigratedFrom: '舊版班級明細網址' }}
    />
  );
}

function LegacyEnglishTestStudentRedirect() {
  const { studentId } = useParams();
  return (
    <Navigate
      to={`/admin/learning-journey/students/${studentId}`}
      replace
      state={{ adminMigratedFrom: '舊版英檢學生頁' }}
    />
  );
}

function AnalyticsStudentProfileRedirect() {
  const { studentId } = useParams();
  const location = useLocation();
  const source = new URLSearchParams(location.search);
  const target = new URLSearchParams();
  const semesterId = source.get('semesterId') || source.get('semester') || source.get('toSemester') || source.get('fromSemester');
  if (semesterId) target.set('semesterId', semesterId);
  const query = target.toString() ? `?${target.toString()}` : '';
  return (
    <Navigate
      to={`/admin/learning-journey/students/${encodeURIComponent(studentId)}${query}`}
      replace
      state={{ adminMigratedFrom: '舊版學生學習歷程查詢' }}
    />
  );
}

function LegacyArchiveNotice({ title, replacementPath, replacementLabel, note }) {
  return (
    <div className="container py-4">
      <div className="card border-warning">
        <div className="card-header bg-warning-subtle fw-semibold">{title}</div>
        <div className="card-body">
          <p className="mb-2">
            此 legacy 入口已封存，不再作為正式維運頁使用。
          </p>
          {note ? <p className="text-muted mb-3">{note}</p> : null}
          <a className="btn btn-primary btn-sm" href={replacementPath}>
            前往{replacementLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

// 內部組件，用於使用 useLocation hook
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  usePageMeta(location.pathname, lang);
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || '');
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [mustResetPassword, setMustResetPassword] = useState(localStorage.getItem('mustResetPassword') === 'true');
  const [weeklyIssue, setWeeklyIssue] = useState(null);
  const [weeklyModalOpen, setWeeklyModalOpen] = useState(false);
  const [, setRegistrationEnabled] = useState(true);

  // 載入報名按鈕開關狀態
  useEffect(() => {
    const loadRegistrationSetting = async () => {
      try {
        const response = await fetchClient('/api/settings/english-test-registration-enabled');
        if (response.ok) {
          const data = await response.json();
          setRegistrationEnabled(data.enabled !== false); // 預設為 true
        }
      } catch (error) {
        console.error('載入報名開關設定錯誤:', error);
        // 發生錯誤時保持預設值（啟用）
      }
    };
    loadRegistrationSetting();
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCurrentWeeklyReport();
        if (cancelled || !data) return;
        setWeeklyIssue(data);
        const dismissKey = data.issueKey || data.slug;
        let dismissed = false;
        try {
          dismissed = Boolean(dismissKey)
            && sessionStorage.getItem('eears-weekly-banner-dismissed') === String(dismissKey);
        } catch {
          dismissed = false;
        }
        setWeeklyModalOpen(!dismissed);
      } catch {
        if (!cancelled) {
          setWeeklyIssue(null);
          setWeeklyModalOpen(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    const onAccessStale = () => {
      try {
        window.dispatchEvent(
          new CustomEvent('eears:toast', {
            detail: { message: '您的帳號權限已更新，請重新登入。', variant: 'warning' },
          })
        );
      } catch (_) {
        // ignore
      }
      handleLogout();
      if (window.location.pathname !== '/login') {
        window.location.href = staffLoginPath(`${window.location.pathname}${window.location.search}`);
      }
    };
    const onAccountDisabled = () => {
      try {
        window.dispatchEvent(
          new CustomEvent('eears:toast', {
            detail: { message: '此帳號已停用，請聯絡管理員。', variant: 'danger' },
          })
        );
      } catch (_) {
        // ignore
      }
      handleLogout();
      if (window.location.pathname !== '/login') {
        window.location.href = staffLoginPath(`${window.location.pathname}${window.location.search}`);
      }
    };
    window.addEventListener('eears:access-stale', onAccessStale);
    window.addEventListener('eears:account-disabled', onAccountDisabled);
    return () => {
      window.removeEventListener('eears:access-stale', onAccessStale);
      window.removeEventListener('eears:account-disabled', onAccountDisabled);
    };
  }, []);

  // 舊首頁 hash 錨點相容：/#announcements → /announcements，/#faq → /faq，/#contact → /about#contact
  useEffect(() => {
    const h = location.hash?.replace('#', '').toLowerCase();
    if (location.pathname !== '/' || !h) return;
    const map = { announcements: '/announcements', faq: '/faq', contact: '/about#contact' };
    if (map[h]) navigate(map[h], { replace: true });
  }, [location.pathname, location.hash, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('teacherName');
    localStorage.removeItem('mustResetPassword');
    setToken('');
    setUserRole('');
    setUsername('');
    setMustResetPassword(false);
  };

  const showWeeklyHomeModal = weeklyModalOpen && location.pathname === '/' && weeklyIssue;
  const isAdminLikePage =
    location.pathname.startsWith('/admin') ||
    location.pathname === '/login' ||
    location.pathname === '/forbidden';
  const isScrollWorldPage =
    location.pathname === '/scrollworldtest' ||
    location.pathname === '/campus-journey-draft' ||
    location.pathname === '/scroll-world-packs';
  const [isDesktopHome, setIsDesktopHome] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 861px)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 861px)');
    const onChange = () => setIsDesktopHome(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // 週報僅在手機首頁顯示；桌面／平板保留沉浸式首頁、不顯示週報
  const showWeekly = showWeeklyHomeModal && !isDesktopHome;

  const publicShellBackground = isScrollWorldPage
    ? {
        background:
          location.pathname === '/campus-journey-draft' || location.pathname === '/scroll-world-packs'
            ? '#f7f9fc'
            : '#f5ede0',
      }
    : {
        background:
          'radial-gradient(circle at top left, rgba(42, 93, 159, 0.05), transparent 30rem), linear-gradient(180deg, #fbfbfa 0%, #f7f3ed 100%)',
      };

  return (
    <>
      <ScrollToTop />
      {/* Phase 3 無障礙：跳過連結（鍵盤第一個可聚焦元素） */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      {/* 自定義滾動條樣式 */}
      <style>
        {`
          .scrollable-content::-webkit-scrollbar {
            width: 6px;
          }
          .scrollable-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          .scrollable-content::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
          }
          .scrollable-content::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          @media (max-width: 480px) {
            .scrollable-content {
              padding-right: 0.25rem;
            }
          }
        `}
      </style>
      <div
        className={`app-wrapper${location.pathname === '/' || location.pathname === '/hometest' ? ' app-wrapper--home' : ''}${location.pathname === '/about' ? ' app-wrapper--about' : ''}${isScrollWorldPage ? ' app-wrapper--scrollworld' : ''}`}
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          ...(isAdminLikePage ? {
            backgroundImage: 'url("/images/bg-pattern2.png")',
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            backgroundAttachment: 'fixed',
          } : publicShellBackground),
        }}
      >
        <PublicLayout
          homeBanner={
            showWeekly ? (
              <WeeklyHomeBanner
                weekly={weeklyIssue}
                onDismiss={() => {
                  try {
                    const key = weeklyIssue?.issueKey || weeklyIssue?.slug;
                    if (key) sessionStorage.setItem('eears-weekly-banner-dismissed', String(key));
                  } catch {
                    // ignore storage errors
                  }
                  setWeeklyModalOpen(false);
                }}
              />
            ) : null
          }
        >
          <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/hometest" element={<HomeImmersiveTestPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/weekly/preview/:token" element={<WeeklyPreviewPage />} />
            <Route path="/weekly" element={<WeeklyPage />} />
            <Route path="/weekly/:issueKey" element={<WeeklyPage />} />
            <Route path="/activities" element={<ActivitiesPage />} />
            <Route path="/practice/word-bridge" element={<WordBridgePage />} />
            <Route path="/practice/listening-ladder" element={<ListeningLadderPage />} />
            <Route path="/practice/vocabulary-depth" element={<VocabularyDepthPage />} />
            <Route path="/practice/vocabulary-size" element={<VocabularySizePage />} />
            <Route path="/practice/speaking-diagnostic" element={<SpeakingDiagnosticPage />} />
            <Route path="/practice/speaking-check" element={<SpeakingCheckPage />} />
            <Route path="/practice/speaking-host/login" element={<SpeakingHostLoginPage />} />
            <Route path="/practice/speaking-host" element={<SpeakingHostPage />} />
            {/* Legacy redirects */}
            <Route path="/activities/word-bridge" element={<Navigate to="/practice/word-bridge" replace />} />
            <Route path="/activities/games/listening-ladder" element={<Navigate to="/practice/listening-ladder" replace />} />
            <Route path="/activities/games/vocabulary-depth" element={<Navigate to="/practice/vocabulary-depth" replace />} />
            <Route path="/activities/games/vocabulary-size" element={<Navigate to="/practice/vocabulary-size" replace />} />
            <Route path="/guides/activity-phrasebook" element={<ActivityPhrasebookPage />} />
            <Route path="/guides/activity-phrasebook/:activityType" element={<ActivityPhrasebookPage />} />
            <Route path="/activities/english-table/topics" element={<EnglishTableTopicsPage />} />
            <Route path="/activities/:slug" element={<ActivityDetailPage />} />
            <Route path="/my-reservations" element={<RequireStudentAuth><MyReservationsPage /></RequireStudentAuth>} />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/announcements/:idOrSlug" element={<AnnouncementDetailPage />} />
            <Route path="/notifications" element={<NotificationPage />} />
            <Route path="/learning-resources" element={<LearningResourcesPage />} />
            <Route path="/regulations-forms" element={<RegulationsFormsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/scrollworldtest" element={<Navigate to="/" replace />} />
            <Route path="/campus-journey-draft" element={<CampusJourneyDraftPage />} />
            <Route path="/scroll-world-packs" element={<ScrollWorldPacksPreviewPage />} />
            <Route path="/course-guide" element={<CourseGuidePage />} />
            <Route path="/contact" element={<Navigate to="/about#contact" replace />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/survey" element={<Navigate to="/survey/choice" replace />} />
            <Route path="/rules" element={<Navigate to="/faq" replace />} />
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route
              path="/login"
              element={
                <LoginPage
                  onLoginSuccess={(newToken, role, user, teacherName, needReset) => {
                    setToken(newToken);
                    setUserRole(role);
                    setUsername(user);
                    setMustResetPassword(!!needReset);
                    localStorage.setItem('token', newToken);
                    localStorage.setItem('userRole', role);
                    localStorage.setItem('username', user);
                    if (teacherName) {
                      localStorage.setItem('teacherName', teacherName);
                    }
                    localStorage.setItem('mustResetPassword', needReset ? 'true' : 'false');
                  }}
                />
              }
            />
            <Route
              path="/admin"
              element={
                token ? (
                  <AdminLayout 
                    token={token} 
                    userRole={userRole} 
                    username={username} 
                      mustResetPassword={mustResetPassword}
                      setMustResetPassword={setMustResetPassword}
                    onLogout={handleLogout} 
                  />
                ) : (
                  <Navigate to={staffLoginPath(`${location.pathname}${location.search}`)} replace />
                )
              }
            >
              <Route index element={<AdminRoleIndex />} />
              <Route path="dashboard" element={<AdminDashboardProduct />} />
              {/* Phase 1：IA 別名，導向既有頁面 */}
              <Route path="events" element={<AdminLegacyRedirect to="/admin/operations" fromLabel="舊版 /admin/events" />} />
              <Route path="surveys/settings" element={<AdminLegacyRedirect to="/admin/survey-rules" fromLabel="問卷設定（舊網址）" />} />
              <Route path="accounts" element={<AdminLegacyRedirect to="/admin/account" fromLabel="帳號管理（舊網址）" />} />
              <Route path="system/settings" element={<AdminLegacyRedirect to="/admin/settings/system" fromLabel="系統設定（舊網址）" />} />
              <Route path="english-tests/tracking" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢追蹤（舊網址）" />} />
              <Route path="english-tests" element={<AdminLegacyRedirect to="/admin/english-test" fromLabel="英檢管理（舊網址）" />} />
              <Route path="operations/participation" element={<AdminEventParticipationStatsPage />} />
              <Route path="operations/:eventId/checkin-kiosk" element={<AdminEventCheckinKioskPage />} />
              <Route path="operations/:eventId" element={<AdminEventDetailPage />} />
              <Route path="et-grouping/settings" element={<AdminEtGroupingSettingsPage />} />
              <Route path="et-grouping/tasks" element={<AdminEtTaskTemplatesPage />} />
              <Route path="et-grouping/reports" element={<AdminEtGroupingReportsPage />} />
              <Route path="et-grouping/payroll" element={<AdminEtLeaderPayrollPage />} />
              <Route path="et-grouping/student-trends" element={<AdminEtStudentTrendsPage />} />
              <Route path="et-grouping/my-sessions" element={<AdminEtLeaderSessionsPage />} />
              <Route path="operations" element={<AdminHome />} />
              <Route path="classes/credit-allocation" element={<AdminClassCreditAllocationPage />} />
              <Route path="classes/:classId/bestep" element={<ClassBestepOverview />} />
              <Route path="classes/:classId" element={<ClassDetail />} />
              <Route path="classes" element={<ClassOverview />} />
            <Route path="teachers/dashboard" element={<TeacherDashboardPage />} />
              <Route path="bestep/import" element={<AdminLegacyRedirect to="/admin/english-test/import" fromLabel="BESTEP 匯入（舊網址）" />} />
              <Route path="violations" element={<ViolationManagement />} />
              <Route path="survey-module/:surveyId/responses" element={<SurveyAdminResponsesPage />} />
              <Route path="survey-module/:surveyId/stats" element={<SurveyAdminStatsPage />} />
              <Route path="survey-module" element={<SurveyAdminModulePage />} />
              <Route path="survey-center" element={<AdminSurveyCenterPage />} />
              <Route path="survey-rules" element={<AdminSurveyRulesPage />} />
              <Route path="survey-responses" element={<AdminSurveyResponsesPage />} />
              <Route path="survey-responses/:surveyId" element={<AdminSurveyResponsesPage />} />
              <Route path="survey-analytics" element={<AdminSurveyAnalyticsPage />} />
              <Route path="survey-analytics/:surveyId" element={<AdminSurveyAnalyticsPage />} />
              <Route path="survey-health" element={<AdminSurveyDataHealthPage />} />
              <Route path="survey-answer-mappings" element={<AdminSurveyAnswerMappingPage />} />
              <Route
                path="surveys"
                element={(
                  <LegacyArchiveNotice
                    title="Legacy 問卷管理已封存"
                    replacementPath="/admin/survey-center"
                    replacementLabel="問卷中心"
                    note="請勿再於此建立或維護新問卷。正式流程：問卷中心（建立／發布）→ 啟用規則 → 作答紀錄／統計分析。舊頁僅保留歷史脈絡。"
                  />
                )}
              />
              <Route path="survey-settings" element={<AdminLegacyRedirect to="/admin/survey-rules" fromLabel="問卷設定（舊網址）" />} />
              <Route path="announcements" element={<AnnouncementManagementPage />} />
              <Route path="announcements/new" element={<AnnouncementEditorPage />} />
              <Route path="announcements/:id/edit" element={<AnnouncementEditorPage />} />
              <Route path="weekly-reports" element={<AdminWeeklyReportPage />} />
              <Route path="weekly-reports/:id/edit" element={<AdminWeeklyReportEditorPage />} />
              <Route path="student-content" element={<StudentContentHubPage />} />
              <Route path="site-content" element={<AdminLegacyRedirect to="/admin/student-content?area=home" fromLabel="網站內容（舊網址）" />} />
              <Route path="page-content" element={<PageContentLegacyRedirect />} />
              <Route path="logs" element={<AdminAuditLogsPage />} />
              <Route path="settings/system" element={<SystemSettingsPage />} />
              <Route path="settings/event-types" element={<EventTypesAdminPage />} />
              <Route path="settings/email-templates" element={<AdminEmailTemplatesPage />} />
              <Route path="diagnostics" element={<InternalDiagnosticsPage />} />
              <Route path="ops-scripts" element={<AdminOpsScriptsPage />} />
              <Route path="trip-counts" element={<TripCountPage />} />
              <Route path="trip-counts/:tripId" element={<TripCountPage />} />
              <Route path="english-test" element={<EnglishTestManagement />} />
              {/* legacy route (redirect only)
                  kept for backward compatibility
                  DO NOT use for new features */}
              <Route path="english-test-tracking" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢追蹤（舊網址）" />} />
              <Route path="english-test-tracking-v2" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢追蹤 v2（舊網址）" />} />
              <Route path="learning-journey-center" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="學習歷程中心（舊網址）" />} />
              {/* legacy route (redirect only)
                  kept for backward compatibility
                  DO NOT use for new features */}
              <Route path="english-test-v2" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢 v2（舊網址）" />} />
              <Route path="english-test-v2/students" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢 v2 學生列表（舊網址）" />} />
              <Route path="english-test-v2/students/:studentId" element={<LegacyEnglishTestStudentRedirect />} />
              <Route path="english-test-tracking/students" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢追蹤學生列表（舊網址）" />} />
              <Route path="english-test-tracking/students/:studentId" element={<LegacyEnglishTestStudentRedirect />} />
              <Route path="english-test-tracking/student-timeline/:studentId" element={<LegacyEnglishTestStudentRedirect />} />
              <Route path="english-test-tracking/*" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢追蹤（舊網址）" />} />
              <Route path="english-test-v2/*" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="英檢 v2（舊網址）" />} />
              <Route path="learning-journey-center/*" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="學習歷程中心（舊網址）" />} />
              <Route path="english-test/import" element={<EnglishTestImportHubPage />} />
              <Route path="import-center" element={<ImportCenterPage />} />
              <Route path="import-center/runs" element={<ImportRunHistoryPage />} />
              <Route path="learning-journey/import" element={<LearningJourneyImportHubPage />} />
              <Route path="learning-journey/ewl-sync" element={<AdminEwlSyncPage />} />
              <Route path="learning-journey" element={<LearningJourneyDashboardPage />} />
              <Route path="learning-journey/operations" element={<LearningJourneyOperationsPage />} />
              <Route path="learning-journey/students/:studentId" element={<LearningJourneyStudentProfilePage />} />
              <Route path="speaking-diagnostic" element={<SpeakingDiagnosticAdminPage />} />
              <Route path="learning-analytics" element={<LearningAnalyticsLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<LearningAnalyticsOverviewPage />} />
                <Route path="kpi-report" element={<LearningAnalyticsKpiReportPage />} />
                <Route path="cohorts" element={<LearningAnalyticsCohortsPage />} />
                <Route path="offerings" element={<LearningAnalyticsOfferingsPage />} />
                <Route path="resources" element={<LearningAnalyticsResourcesPage />} />
                <Route path="skills" element={<LearningAnalyticsSkillsPage />} />
                <Route path="b1-plus" element={<LearningAnalyticsB1PlusPage />} />
                <Route path="skills/:studentId" element={<LearningAnalyticsStudentDetailPage focus="skills" />} />
                <Route path="explore" element={<LearningAnalyticsStudentExplorerPage />} />
                <Route path="student-explorer" element={<Navigate to="/admin/learning-analytics/explore" replace />} />
                <Route path="tracking-cohorts" element={<LearningAnalyticsTrackingCohortsPage />} />
                <Route path="tracking-cohorts/:id" element={<LearningAnalyticsTrackingCohortsPage />} />
                <Route path="students" element={<LearningAnalyticsStudentJourneyPage />} />
                <Route path="students/:studentId" element={<LearningAnalyticsStudentDetailPage />} />
                <Route path="raw-data" element={<LearningAnalyticsRawDataPage />} />
                <Route path="insights" element={<Navigate to="overview" replace />} />
                <Route path="model-runs" element={<Navigate to="resources" replace />} />
                <Route path="settings" element={<LearningAnalyticsSettingsPage token={token} />} />
              </Route>
              <Route path="english-learning-passports" element={<EnglishLearningPassportsAdminPage />} />
              <Route path="english-learning-passports/:id" element={<EnglishLearningPassportDetailPage />} />
              <Route path="analytics/student/:studentId" element={<AnalyticsStudentProfileRedirect />} />
              <Route path="analytics/students" element={<AdminLegacyRedirect to="/admin/learning-journey" fromLabel="學生學習歷程查詢（舊網址）" />} />
            <Route path="analytics" element={<AnalyticsHubPage />} />
            <Route path="analytics/overview" element={<AdminAnalyticsPage />} />
            <Route path="analytics/risk" element={<RiskDetectionPage />} />
              <Route path="analytics/trends" element={<AdminLegacyRedirect to="/admin/analytics/overview" fromLabel="趨勢分析（舊網址）" />} />
              <Route path="reports" element={<ReportPage />} />
              <Route path="analytics/teacher-impact" element={<AdminLegacyRedirect to="/admin/analytics/overview" fromLabel="班級行政綜合變化（舊網址）" />} />
              <Route path="account" element={<AccountManagement />} />
              <Route path="account/reset" element={<ForceResetPassword />} />
            </Route>
            <Route path="/survey/choice" element={<SurveyChoicePage />} />
            <Route path="/survey/:surveyId" element={<SurveyPage />} />
            <Route path="/register/english-test" element={<RequireStudentAuth><EnglishTestRegistrationPage /></RequireStudentAuth>} />
            <Route path="/register/english-test/group" element={<LearningPartnerRegistrationPage />} />
            <Route path="/register/english-test/group/status/:teamId" element={<LearningPartnerStatusPage />} />
            <Route path="/register/english-test/group/approve" element={<LearningPartnerApprovePage />} />
            <Route path="/student/login" element={<StudentLoginPage />} />
            <Route path="/student/forgot-password" element={<StudentForgotPasswordPage />} />
            <Route path="/student/register" element={<StudentRegisterPage />} />
            <Route path="/student/account" element={<RequireStudentAuth><StudentAccountPage /></RequireStudentAuth>} />
            <Route path="/student/progress" element={<RequireStudentAuth><StudentProgressPage /></RequireStudentAuth>} />
            <Route path="/student/speaking-portfolio" element={<RequireStudentAuth><SpeakingPortfolioPage /></RequireStudentAuth>} />
            <Route path="/student/class-credit-allocation" element={<RequireStudentAuth><ClassCreditAllocationPage /></RequireStudentAuth>} />
            <Route path="/student/english-learning-passport" element={<RequireStudentAuth><EnglishLearningPassportPage /></RequireStudentAuth>} />
            <Route path="/student/english-learning-passport/submissions/:id" element={<RequireStudentAuth><EnglishLearningPassportSubmissionPage /></RequireStudentAuth>} />
            <Route path="/student/english-learning-passport/certification" element={<RequireStudentAuth><EnglishLearningPassportCertificationPage /></RequireStudentAuth>} />
            <Route
              path="/admin/classes/:classId/detail"
              element={<LegacyClassDetailRedirect token={token} />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
        </PublicLayout>
      </div>
    </>
  );
}

// 主要的 App 組件
function AppInner() {
  return (
    <Router>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </Router>
  );
}

// 依需求：在 App.js 內掛全域 Provider（不影響既有路由/功能）
export default function App() {
  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  );
}
